import type { RulebookOwnerType } from "@next-platform/packages-ts-types";
import React from "react";

import Trans from "@/components/Trans/Trans";

/** What a Rulebook is being attached to: a character or persona (saved with its form), or a single chat (pinned at once). */
export type RulebookTarget = RulebookOwnerType | "conversation";

type RulebookOwnerCopyProps = {
  ownerType: RulebookTarget;
  /** `section`: under the Attach / Replace buttons in the owner's form; `picker`: at the top of the picker. */
  place: "section" | "picker";
};

/**
 * The only picker copy that depends on what the Rulebook is attached to. A persona's Rulebook wins over the
 * character's (conversation pin → persona → character), and both are pinned when a chat starts.
 */
const COPY: Record<RulebookTarget, Record<RulebookOwnerCopyProps["place"], React.ReactElement>> = {
  character: {
    section: (
      <Trans
        i18nKey="chatbot:form.section.rulebook.description"
        defaults="Rulebooks add meters, like affection or trust, that change as users chat and shape how the character responds."
      />
    ),
    picker: (
      <Trans
        i18nKey="chatbot:form.section.rulebook.modal.description"
        defaults="Pick a Rulebook. New chats with this character use it; chats that already started keep the Rulebook they began with."
      />
    ),
  },
  conversation: {
    section: (
      <Trans
        i18nKey="chat:rulebook.attach.description"
        defaults="Rulebooks add meters, like affection or trust, that change as you chat. This chat follows its Rulebook instead of the character's."
      />
    ),
    picker: (
      <Trans
        i18nKey="chat:rulebook.attach.modal.description"
        defaults="Pick a Rulebook for this chat. It starts with your next message, and it can't be swapped for another later."
      />
    ),
  },
  persona: {
    section: (
      <Trans
        i18nKey="personas:form.rulebook.description"
        defaults="Rulebooks add meters, like affection or trust, that change as you chat. Chats where you use this persona follow its Rulebook instead of the character's."
      />
    ),
    picker: (
      <Trans
        i18nKey="personas:form.rulebook.modal.description"
        defaults="Pick a Rulebook. New chats where you use this persona use it instead of the character's; chats that already started keep the Rulebook they began with."
      />
    ),
  },
};

export default function RulebookOwnerCopy({ ownerType, place }: RulebookOwnerCopyProps) {
  return COPY[ownerType][place];
}
